import { random } from "lodash";
import { ELAVON_GATEWAY_ID, FREEDOMPAY_GATEWAY_ID } from "../../app-constants";
import { isDevelopmentEnv } from "./config";
import { loadExternalCdnScript } from "./misc";

/**
 * List of encryption types
 * @type {{CNP: string}}
 */
export const ENCRYPTION_TYPE = {
  CARD_NOT_PRESENT: "cnp"
};

/**
 * Check if a payment is PPI according to PaymentType
 * @param paymentType
 * @returns boolean
 */
export const isPPIPayment = (paymentType: number): boolean => ![1, 7].includes(paymentType);

/**
 * Get last 4 digits of a card
 * @param digits:string
 * @returns string
 */
export const getCardLastFour = (digits: string): string => {
  return digits && digits.length > 0 ? digits.slice(-4).toString() : "";
};

/**
 * Get padded/masked card number
 * @param digits:string
 * @returns string
 */
export const getMaskedCardNumber = (digits: string): string => {
  return digits && digits.length > 0 ? digits.slice(-4).padStart(16, "*") : "";
};

/**
 * Get padded/masked card number
 *
 * @param email
 * @returns a string in the format of j*****@spoton.com
 */
export const getMaskedEmail = (email: string): string => {
  const [username, domain] = email.split("@");
  const maskedUsername = username.charAt(0) + "*".repeat(4);
  return `${maskedUsername}@${domain}`;
};

/**
 * Returns a random invoice number
 * @returns string
 */
export const getRandomInvoiceNumber = (): string => {
  const invoiceNumber = random(0, 9999999);

  return invoiceNumber.toString().padStart(7, "0000000");
};

/**
 * Returns a number with max digits
 * @param length indicates the max length of the number in digits
 *
 * @return {number}
 */
export const getRandomNumberbyMaxLength = (length: number): number => {
  const maxValue = Math.pow(10, length) - 1;
  return random(0, maxValue);
};

/***
 * Loads external payment script
 * @param tokenizedGatewayId
 * @returns {Promise<void>}
 */
export function loadPaymentGatewayScripts(tokenizedGatewayId: number) {
  let domain,
    src = "";

  const isProd = !isDevelopmentEnv();

  switch (tokenizedGatewayId) {
    case FREEDOMPAY_GATEWAY_ID:
      unloadPaymentGatewayScripts(ELAVON_GATEWAY_ID);
      domain = isProd ? "hpc.freedompay.com" : "hpc.uat.freedompay.com";
      src = `https://${domain}/api/v1.4/cdn/applepay_min.js`;
      break;

    case ELAVON_GATEWAY_ID:
      unloadPaymentGatewayScripts(FREEDOMPAY_GATEWAY_ID);
      domain = isProd ? "api.convergepay.com" : "api.demo.convergepay.com";
      src = `https://${domain}/hosted-payments/Checkout.js`;
      break;

    default:
      //do nothing if there's no gateway
      return Promise.resolve();
  }

  loadExternalCdnScript(src, `extPayScript_${tokenizedGatewayId}`);
}

/**
 * Unloads external payment script
 *
 * @returns {Promise<void>}
 */
export function unloadPaymentGatewayScripts(tokenizedGatewayId: number) {
  const scriptTag = document.getElementById(`extPayScript_${tokenizedGatewayId}`);
  scriptTag && scriptTag.remove();
}

/**
 * Checks an IFrame for undesired tags
 * @param strIframe:string
 * @returns boolean
 */
export const FPIFrameValidation = (strIframe: string): boolean => {
  //Empty paymentIframe MAPI and non FP will exit here
  if (!strIframe) return true;

  //Looking for an https source
  if (!strIframe.match('src="https://')) return false;

  //validate there's no script tag nor html tag
  if (strIframe.match("<script|<html")) return false;

  //Looking for more than 1 src prop
  const matchArray = strIframe.match(/src="/g) || [];
  if (matchArray.length > 1) return false;

  return true;
};
